// React, Next
import { useTranslation } from 'next-i18next'
import { useContext, useRef } from 'react'
import { StaticImport } from 'next/dist/shared/lib/get-img-props'
import Image from 'next/image'
import { useRouter } from 'next/router'
import nextI18nextConfig from 'next-i18next.config'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Custom
import { GlobalContext } from 'context/GlobalContextProvider'
import Section from '@/components/sections/Section'
import { translateHref } from 'assets/functions/links'

// Img - each card carries the hero image of the page it opens, in the crop
// that suits the card's 2.5:1 frame where one exists.
import OtCybersecurity from 'public/img/services/shop_floor.webp'
import OtAwarenessTraining from 'public/img/services/ot_awareness_training_compact.webp'
import TestEquipmentModernization from 'public/img/services/test_machine_retrofit_compact.webp'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import classes from './OurServicesSection-CSS'
import {
  getFontBody,
  getFontH2,
  getFontH4,
  getFontH5
} from '@/assets/theme/fonts'
import { textAlign } from '@mui/system'

export default function OurServicesSection() {
  const { t } = useTranslation('services')
  const { screenDimensions } = useContext(GlobalContext)

  const router = useRouter()
  const currentLocale =
    router.query.locale || nextI18nextConfig.i18n.defaultLocale

  const ref: any = useRef(null)

  // Three services, so the grid never goes wider than three columns.
  const getGrid = () => {
    const w = screenDimensions.width
    if (w > breakpoints.desktopToMobile) return 'repeat(3, 1fr)'
    if (w > breakpoints.mobileMid) return 'repeat(2, 1fr)'
    return 'repeat(1, 1fr)'
  }

  const getImgWidth = () => {
    const w = screenDimensions.width
    if (w > breakpoints.desktopToMobile) return '33vw'
    if (w > breakpoints.mobileMid) return '50vw'
    return '100vw'
  }

  const getImageHeight = () => {
    try {
      return ref.current.getBoundingClientRect().width / 2.5
    } catch {
      return 150
    }
  }

  // *** Section ***
  const getCard = (p: {
    id: number
    title: string
    img: string | StaticImport
    alt: string
    href: string
  }) => (
    <Box
      onClick={() => router.push(p.href)}
      sx={{
        ...classes.cardBox,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Image
        alt={p.alt}
        src={p.img}
        style={{
          width: getImgWidth(),
          height: getImageHeight(),
          objectFit: 'cover',
          borderRadius: '5px',
          display: 'block'
        }}
        priority={false}
      />
      <Typography
        variant="h4"
        fontSize={getFontH5()}
        sx={{
          ...classes.typeH4,
          color: themeColors.bg.start,
          background: '#ffffffdf',
          zIndex: 5,
          position: 'absolute',
          bottom: 0,
          width: '100%',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {p.title}
      </Typography>
    </Box>
  )

  const getHeader = () => {
    const align =
      screenDimensions.width < breakpoints.mobileSmall ? 'center' : 'left'

    return (
      <Box sx={{ zIndex: 2 }}>
        <Typography
          variant="h1"
          fontSize={getFontH2()}
          sx={{
            color: themeColors.gray.mid,
            textAlign: align,
            fontWeight: 400,
            marginBottom: '1rem'
          }}
        >
          {t('services.title')}
        </Typography>

        <Typography
          component="p"
          fontSize={getFontH5()}
          sx={{
            color: themeColors.kroemtech.dunkelblau,
            textAlign: align,
            fontWeight: 600,
            lineHeight: 1.4,
            marginBottom: '2rem'
          }}
        >
          {t('services.subtitle')}
        </Typography>

        {[t('services.intro1'), t('services.intro2'), t('services.intro3')].map(
          paragraph => (
            <Typography
              key={paragraph}
              component="p"
              fontSize={getFontBody()}
              sx={{
                color: themeColors.gray.mid,
                textAlign: align,
                lineHeight: 1.7,
                maxWidth: '52rem',
                marginBottom: '1rem',
                '&:last-of-type': { marginBottom: '2.5rem' }
              }}
            >
              {paragraph}
            </Typography>
          )
        )}
      </Box>
    )
  }

  const getSection = () => (
    <Box
      gridTemplateColumns={getGrid()}
      sx={{
        ...classes.gridBox
      }}
    >
      {getCard({
        id: 6,
        title: t('services.list.card6'),
        alt: 'OT Cybersecurity',
        img: OtCybersecurity,
        href: translateHref(currentLocale, 'ot-cybersecurity')
      })}
      {getCard({
        id: 7,
        title: t('services.list.card7'),
        alt: 'OT Awareness Training',
        img: OtAwarenessTraining,
        href: translateHref(currentLocale, 'ot-awareness-training')
      })}
      {getCard({
        id: 8,
        title: t('services.list.card8'),
        alt: 'Test Equipment Modernization',
        img: TestEquipmentModernization,
        href: translateHref(currentLocale, 'test-equipment-modernization')
      })}
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <>
      <Section paddingTop="6rem" content={getHeader()} />
      <Section marginBottom="4rem" content={getSection()} />
    </>
  )
}
